import { useTranslator } from "@eo-locale/react";

import toArray from "lodash/toArray";

import { ChangeEvent, useEffect } from "react";

import { CommonCheckboxQuestion } from "@/storybook-components";
import type { Question } from "@/types";
import {
  removeSurveyAnswers,
  setSurveyAnswers
} from "@/redux/features/survey/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { spacing, textStyles } from "./question-styles";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

interface CheckBoxQuestionProps {
  question: Question;
}

const CheckBoxQuestion = ({ question }: CheckBoxQuestionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, options, saving } = survey;

  const error = errors[question.id] ?? {};
  const answerId = answers[question.id]?.id ?? [];
  const answerValue = answers[question.id]?.value ?? "";

  // ToDo: remove from BE
  const sortedOptions = toArray(options)
    .filter((option) => question.id === option.questionId)
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

  useEffect(() => {
    if (answers[question.id]?.id.length <= 0) {
      dispatch(
        removeSurveyAnswers({
          questionId: question.id
        })
      );
      return;
    }
  }, [answers, dispatch, question.id]);

  const handleChange = (value: Array<string>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          id: value.map(Number),
          ...(answers[question.id]?.value && {
            value: answers[question.id].value
          })
        }
      })
    );
  };

  const handleOtherChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          id: answerId,
          value: e.target.value
        }
      })
    );
  };

  const showTextAnswer = sortedOptions.find(
    (option) => option.supportsTextAnswer && answerId.indexOf(option.id) > -1
  );

  const minImageWidth = 300;

  return (
    <CommonCheckboxQuestion
      layoutProps={{ width: "100%", as: "fieldset" }}
      isInvalid={!!error.code}
      isRequired={question.required}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      questionLabelType="legend"
      contentLayoutProps={{ py: 4 }}
      isHorizontal={question.horizontal}
      answer={answerId}
      minImageWidth={minImageWidth}
      size="xlIconWithLabelStyle"
      handleChange={handleChange}
      answerStackSpacing={spacing.answerStack}
      options={sortedOptions}
      checkboxAnswerLimit={question.checkboxAnswersLimit}
      answerProps={{ ...textStyles?.content }}
      showTextAnswer={showTextAnswer}
      isDisabled={saving}
      otherAnswer={answerValue}
      handleOtherAnswer={handleOtherChange}
      otherAnswerClassName="ehq-surveyTextarea"
      otherAnswerSize="lg"
      otherAnswerProps={{ ...textStyles?.content, mt: 2 }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
    />
  );
};

export default CheckBoxQuestion;
